import { enableChartsExporting } from '@ecosystem/export-utils';
import { InsightRendererProps } from '@ecosystem/newron-client-collections';
import React, { FC } from 'react';
import { Provider } from 'react-redux';
import { renderLoading } from '@pages/Loading';
import { getExportPerformanceAnalysisLineChart } from '@services/Chart.service';
import Store from '../../../state/Store';
import MultiChartExportProvider from '@hooks/export/utilities/MultiChartExportProvider';
import PerformanceAnalysisMarketCard, {
  PerformanceAnalysisMarketCardProps,
} from '../../performance-analysis-market-card/PerformanceAnalysisMarketCard';
import PerformanceAnalysisRetailerCard, {
  PerformanceAnalysisRetailerCardProps,
} from '../../performance-analysis-retailer-card/PerformanceAnalysisRetailerCard';
import { PerformanceAnalysisLineChartInsight } from './Model';
import { modifiedPAORSegmentExportGrandTotalToCamelCaseTotalValue } from '@utils/TotalUtils';
import { PageType } from '@utils/pageConfig/Types';
import {
  processExploreMarketLineChartData,
  processExploreRetailerLineChartData,
} from '@data-visualization/utilities/line-chart/FormatLineChartData';
import { getExploreTrends } from '@services/explore/Explore.service';
import { CollectionInsightType } from '@components/collections/Model';
import { useLineChartInsight } from '@utils/collectionUtils/hooks/UseLineChartInsight';

enableChartsExporting();

type PerformanceAnalysisLineChartInsightRendererType = InsightRendererProps<PerformanceAnalysisLineChartInsight>;

const PerformanceAnalysisLineChartInsightContainer: FC<PerformanceAnalysisLineChartInsightRendererType> = ({
  insight,
  onLoaded,
  onExportReady,
}) => {
  const { apiParams, selectedLineChartSaleType, selectedRetailerKpiOption, currencySymbol } = insight.data;

  const { isLoading, chartData, getWarningBannerWrapper } = useLineChartInsight({
    apiParams,
    dataType: insight.dataType,
    period: apiParams.period,
    pageType: PageType.EXPLORE,
    marketChartKey: 'MARKET',
    retailerChartKey: 'RETAILER',
    collectionInsightType: CollectionInsightType.ExploreLineChart,
    onLoaded,
    onExportReady,
    apiRequest: async ({ getPeriodFromLabel, isUsingCollectionLiveData }) => {
      return apiParams.segment === 'Category'
        ? getExploreTrends({
            ...apiParams,
            period: getPeriodFromLabel,
            isUsingCollectionLiveData: isUsingCollectionLiveData,
          })
        : getExportPerformanceAnalysisLineChart({
            ...apiParams,
            period: getPeriodFromLabel,
            isUsingCollectionLiveData: isUsingCollectionLiveData,
          });
    },
    processMarketData: (data) => {
      const modifiedData = data ? modifiedPAORSegmentExportGrandTotalToCamelCaseTotalValue(data) : null;
      return modifiedData
        ? processExploreMarketLineChartData(modifiedData.kpiDataDto, selectedLineChartSaleType).filter(Boolean)
        : null;
    },
    processRetailerData: (data) => {
      const modifiedData = data ? modifiedPAORSegmentExportGrandTotalToCamelCaseTotalValue(data) : null;
      return modifiedData
        ? processExploreRetailerLineChartData(
            modifiedData.kpiDataDto,
            selectedLineChartSaleType,
            selectedRetailerKpiOption,
          ).filter(Boolean)
        : null;
    },
  });

  const modifiedData = chartData
    ? modifiedPAORSegmentExportGrandTotalToCamelCaseTotalValue(chartData)
    : {
        salesRecordsFound: false,
        kpiDataDto: [],
      };

  const marketCardProps: PerformanceAnalysisMarketCardProps = {
    data: modifiedData,
    selectedPeriodicity: apiParams.periodType,
    currencySymbol: currencySymbol,
    selectedLineChartSaleType: selectedLineChartSaleType,
    noResultHandler: () => {},
  };

  const retailerCardProps: PerformanceAnalysisRetailerCardProps = {
    data: modifiedData,
    selectedPeriodicity: apiParams.periodType,
    currencySymbol: currencySymbol,
    selectedLineChartSaleType: selectedLineChartSaleType,
    noResultHandler: () => {},
    selectedRetailerOption: selectedRetailerKpiOption,
    setSelectedRetailerOption: () => {},
    onChangeSelectedRetailerOption: () => {},
    excludeFilter: true,
  };

  if (isLoading) {
    return renderLoading();
  }

  return getWarningBannerWrapper(
    <>
      <PerformanceAnalysisMarketCard {...marketCardProps} />
      <PerformanceAnalysisRetailerCard {...retailerCardProps} />
    </>,
  );
};

export const PerformanceAnalysisLineChartInsightRenderer: FC<PerformanceAnalysisLineChartInsightRendererType> = (
  props,
) => {
  return (
    <Provider store={Store}>
      <MultiChartExportProvider>
        <PerformanceAnalysisLineChartInsightContainer {...props} />
      </MultiChartExportProvider>
    </Provider>
  );
};
